import { AutoCoderImportBatch } from '../types/gotypes'

/**
 * Reading an import receipt (E1) the way an operator does.
 *
 * A receipt is written for EVERY fetch, including the ones that failed or came
 * back short — that is the whole point of the audit trail — so the first job of
 * the UI is to say, at a glance, which kind of fetch this was.
 */
export type ImportBatchOutcome =
  | 'failed'
  | 'stalled'
  | 'fetching'
  | 'queued'
  | 'scheduled'
  | 'sending'
  | 'truncated'
  | 'skipped'
  | 'empty'
  | 'ok'

/**
 * How long an unfinalized receipt may keep saying "fetching" before the UI
 * stops believing it. Nothing enforces this server-side — it is a reading of
 * the row, not a state — and it exists because the async ingest owns its run in
 * a goroutine: a goapi restart mid-fetch leaves a receipt open forever, and the
 * user's way out is Retry (which mints a NEW receipt; this one is never
 * reopened).
 */
export const IMPORT_BATCH_STALL_MS = 60 * 60 * 1000

const parsedTime = (value: unknown): number | undefined => {
  if (typeof value !== 'string' || value.trim() === '') return undefined
  const t = Date.parse(value)
  return Number.isNaN(t) ? undefined : t
}

/**
 * A receipt is IN FLIGHT while it carries no finalization marker (migration
 * 000103): the fetch that opened it has not written its outcome yet. An
 * unparseable marker counts as absent — a receipt we cannot read as finished is
 * not one we should render as finished.
 *
 * This is also the polling signal: a list holding any in-flight receipt is a
 * list that is still changing.
 */
export const importBatchIsInFlight = (batch: AutoCoderImportBatch, now: number = Date.now()): boolean => {
  if (isImportRequest(batch)) {
    const outcome = importRequestOutcome(batch, now)
    return outcome === 'queued' || outcome === 'fetching' || outcome === 'stalled' || outcome === 'sending'
  }
  return parsedTime(batch?.finalizedAt) === undefined
}

/**
 * A row that names a driver is a REQUEST on the import queue (migration 000174,
 * design/2026-10-22-import-queue.md) as well as its receipt: its status is the
 * queue's word for where it is. Rows with no driver are the older receipts.
 */
export const isImportRequest = (batch: AutoCoderImportBatch): boolean => (batch?.driver || '').trim() !== ''

/**
 * Where a request is, before its counts mean anything: booked for later
 * (scheduled), waiting for its driver's capacity (queued — a refusal such as
 * X's monthly cap is a reason shown on it, not a failure), running (stalled an
 * hour after it STARTED, not after it was asked for: a request may wait days),
 * fetched and being sent (sending), or failed. A done request reads its counts
 * like any receipt, and its error is a note ("comments are disabled").
 */
const importRequestOutcome = (batch: AutoCoderImportBatch, now: number): ImportBatchOutcome | undefined => {
  switch (batch?.status) {
    case 'queued': {
      const next = parsedTime(batch.nextAttemptAt)
      return next !== undefined && next > now ? 'scheduled' : 'queued'
    }
    case 'running': {
      const started = parsedTime(batch.startedAt)
      return started !== undefined && now - started >= IMPORT_BATCH_STALL_MS ? 'stalled' : 'fetching'
    }
    case 'fetched':
      return 'sending'
    case 'failed':
      return 'failed'
  }
  return undefined
}

/**
 * Priority is deliberate: an error outranks everything (including an unwritten
 * marker — a receipt that already knows how it failed is a failure, not a fetch
 * in progress), an unfinished fetch outranks the counts on its own row,
 * truncation outranks a parse skip, and any of those outranks a healthy-looking
 * count. A fetch that errored after 900 rows is a FAILURE with 900 rows, not a
 * success — the window it claimed is not covered.
 *
 * `parseSkipped` is a coverage hole for exactly the same reason truncation is:
 * the importer HOLDS its cursor when rows in the window could not be parsed,
 * because the ordered prefix is no longer complete. Rendering such a fetch as
 * green "Imported" (or as a neutral "No rows", when every row was skipped) is
 * the trap the truncation banner exists to prevent, so it gets the same warning
 * treatment.
 *
 * `finalizedAt` is what makes the in-flight states observable at all: the
 * funnel columns are NOT NULL DEFAULT 0, so before the marker existed an
 * open receipt read pages=0 fetched=0 error="" — indistinguishable from a clean
 * fetch that found nothing, and both had to surface as `empty`. Now an
 * unfinalized receipt reads as `fetching`, and one still unfinalized an hour
 * after it was requested reads as `stalled`.
 *
 * `now` is a parameter rather than a call to Date.now() inside, so the stall
 * threshold is testable and a table can classify a whole page against one
 * instant.
 */
export const importBatchOutcome = (
  batch: AutoCoderImportBatch,
  now: number = Date.now(),
): ImportBatchOutcome => {
  if (isImportRequest(batch)) {
    const where = importRequestOutcome(batch, now)
    if (where) return where
    if (batch?.truncated) return 'truncated'
    if ((batch?.parseSkipped ?? 0) > 0) return 'skipped'
    if (!batch?.fetched) return 'empty'
    return 'ok'
  }
  if ((batch?.error || '').trim() !== '') return 'failed'
  if (importBatchIsInFlight(batch, now)) {
    const asked = parsedTime(batch?.requestedAt) ?? parsedTime(batch?.createdAt)
    if (asked !== undefined && now - asked >= IMPORT_BATCH_STALL_MS) return 'stalled'
    return 'fetching'
  }
  if (batch?.truncated) return 'truncated'
  if ((batch?.parseSkipped ?? 0) > 0) return 'skipped'
  if (!batch?.fetched) return 'empty'
  return 'ok'
}

export const importBatchOutcomeLabel = (outcome: ImportBatchOutcome): string => {
  switch (outcome) {
    case 'failed':
      return 'Failed'
    case 'stalled':
      return 'Stalled'
    case 'fetching':
      return 'Fetching…'
    case 'queued':
      return 'Waiting'
    case 'scheduled':
      return 'Scheduled'
    case 'sending':
      return 'Sending'
    case 'truncated':
      return 'Truncated'
    case 'skipped':
      return 'Rows skipped'
    case 'empty':
      return 'No rows'
    default:
      return 'Imported'
  }
}

/**
 * MUI Chip colour for an outcome. Truncation and parse skips are warnings, never
 * successes — both mean the window is not fully covered. A stall is a warning
 * too: nothing is wrong with the data, but nothing is coming either, and the
 * row needs a Retry. A fetch still running is informational, not an outcome.
 */
export const importBatchOutcomeColor = (
  outcome: ImportBatchOutcome,
): 'error' | 'warning' | 'info' | 'default' | 'success' => {
  switch (outcome) {
    case 'failed':
      return 'error'
    case 'fetching':
    case 'queued':
    case 'sending':
      return 'info'
    case 'scheduled':
      return 'default'
    case 'stalled':
    case 'truncated':
    case 'skipped':
      return 'warning'
    case 'empty':
      return 'default'
    default:
      return 'success'
  }
}

/**
 * The subset of the stored request payload worth putting in a table cell. The
 * full payload is jsonb of arbitrary shape (Sky's own widget export, with the
 * window and paging injected), so every field is read defensively — a receipt
 * whose query is null, a string, or the wrong types must still render.
 */
export interface ImportQuerySummary {
  report?: string
  pageSize?: number
  page?: number
  filterCount: number
  timeZone?: string
  sort?: string
}

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v : undefined

const num = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined

export const summarizeImportQuery = (query: unknown): ImportQuerySummary => {
  if (!query || typeof query !== 'object' || Array.isArray(query)) {
    return { filterCount: 0 }
  }
  const q = query as Record<string, unknown>

  const filters = Array.isArray(q.filters) ? q.filters : []

  let sort: string | undefined
  const sorts = Array.isArray(q.sorts) ? q.sorts : []
  const first = sorts[0]
  if (first && typeof first === 'object') {
    const s = first as Record<string, unknown>
    const heading = str(s.heading)
    const order = str(s.order)
    if (heading) sort = order ? `${heading} ${order}` : heading
  }

  const summary: ImportQuerySummary = { filterCount: filters.length }
  const report = str(q.report) ?? str(q.reportingEngine)
  if (report) summary.report = report
  const pageSize = num(q.pageSize)
  if (pageSize !== undefined) summary.pageSize = pageSize
  const page = num(q.page)
  if (page !== undefined) summary.page = page
  const timeZone = str(q.timeZone)
  if (timeZone) summary.timeZone = timeZone
  if (sort) summary.sort = sort
  return summary
}

/** One-line rendering of the above; absent parts are omitted, not zero-filled. */
export const formatImportQuerySummary = (query: unknown): string => {
  const s = summarizeImportQuery(query)
  const parts: string[] = []
  // An Apify fetch's query names the post it scraped (and, for the influencer
  // scrape queue, says so): show that rather than a dash.
  if (query && typeof query === 'object') {
    const q = query as Record<string, unknown>
    if (q.purpose === 'influencer') parts.push('influencer')
    if (typeof q.url === 'string' && q.url) {
      parts.push(typeof q.platform === 'string' && q.platform ? `${q.platform} ${q.url}` : q.url)
    }
  }
  if (s.report) parts.push(s.report)
  if (s.page !== undefined) parts.push(`page ${s.page}`)
  if (s.pageSize !== undefined) parts.push(`pageSize ${s.pageSize}`)
  if (s.filterCount > 0) parts.push(`${s.filterCount} filter${s.filterCount === 1 ? '' : 's'}`)
  if (s.sort) parts.push(`sort ${s.sort}`)
  return parts.length > 0 ? parts.join(' · ') : '—'
}

/**
 * Windows are UTC everywhere in this system — the janitor's day-runs, the
 * cursor, the coverage ledger — so they are rendered in UTC rather than in the
 * viewer's local zone, which would silently shift a "2026-02-05" import.
 */
const utcStamp = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || value.trim() === '') return undefined
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return undefined
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(
    d.getUTCHours(),
  )}:${pad(d.getUTCMinutes())}`
}

export const formatImportWindow = (start: unknown, end: unknown): string => {
  const from = utcStamp(start)
  const to = utcStamp(end)
  if (!from && !to) return '—'
  return `${from ?? '…'} → ${to ?? '…'} UTC`
}

/** Absolute UTC timestamp incl. seconds, for the "when" column. */
export const formatImportTimestamp = (value: unknown): string => {
  const stamp = utcStamp(value)
  if (!stamp) return '—'
  const d = new Date(value as string)
  return `${stamp}:${String(d.getUTCSeconds()).padStart(2, '0')} UTC`
}

/** A driver in words (the import queue's Driver column). */
export const importDriverLabel = (driver: string | undefined): string => {
  switch ((driver || '').trim()) {
    case 'apify':
      return 'Apify'
    case 'x':
      return 'X API'
    case 'youtube':
      return 'YouTube API'
    case 'file':
      return 'File'
    default:
      return (driver || '').trim()
  }
}

/** The Imports panel's tabs: '' is every batch but the waiting requests. */
export type ImportTab = '' | 'in_progress' | 'scheduled' | 'done' | 'failed'

export const IMPORT_TABS: { value: ImportTab; label: string; count?: 'inProgress' | 'scheduled' | 'done' | 'failed' }[] = [
  { value: '', label: 'All' },
  { value: 'in_progress', label: 'In progress', count: 'inProgress' },
  { value: 'scheduled', label: 'Scheduled', count: 'scheduled' },
  { value: 'done', label: 'Done', count: 'done' },
  { value: 'failed', label: 'Failed', count: 'failed' },
]
